import PlatformIcon from "../../platforms/components/platform-icon";
import { UtmChangeImpact, type Platform } from "../../platforms/constants";
import {
  getPlatformTitle,
  usePlatformsMetadata,
} from "../../platforms/metadata";
import { type PlatformMetadata } from "../../platforms/types";

const UtmChangeImpactPlatformCell = ({ platform }: { platform: Platform }) => {
  const { getMetadata } = usePlatformsMetadata();
  return (
    <>
      <div className="w-[304px] h-10 bg-slate-50 flex-col justify-end items-start inline-flex">
        <div className="self-stretch grow shrink basis-0 pl-2 justify-between items-center inline-flex">
          <div className="justify-start items-center gap-2 flex">
            <PlatformIcon platform={platform} />
            <div className="text-slate-900 text-sm font-normal font-['TT Commons'] leading-tight">
              {getPlatformTitle(getMetadata(platform))}
            </div>
          </div>
          <div className="w-px self-stretch bg-zinc-200" />
        </div>
        <div className="self-stretch h-px bg-zinc-200" />
      </div>
    </>
  );
};

export const UtmChangeImpactTable = () => {
  const { getMetadata } = usePlatformsMetadata();
  // group all platforms by change impact
  const keyed = getMetadata().reduce((acc, metadata) => {
    if (!metadata.utmChangeImpact) {
      return acc;
    }
    (acc[metadata.utmChangeImpact] = acc[metadata.utmChangeImpact] || []).push(
      metadata,
    );
    return acc;
  }, {} as { [key in UtmChangeImpact]: PlatformMetadata[] });

  // sort lists and get the max length
  const maxLength = Object.values(UtmChangeImpact).reduce((acc, key) => {
    keyed[key].sort((a: PlatformMetadata, b: PlatformMetadata) => {
      const titleA = getPlatformTitle(a);
      const titleB = getPlatformTitle(b);
      return titleA < titleB ? -1 : 1;
    });
    if (keyed[key].length > acc) {
      acc = keyed[key].length;
    }
    return acc;
  }, 0);

  const rows = [] as any;
  for (let i = 0; i < maxLength; i++) {
    rows.push([
      keyed[UtmChangeImpact.HIGH][i] ? keyed[UtmChangeImpact.HIGH][i] : "",
      keyed[UtmChangeImpact.SOMEWHAT][i]
        ? keyed[UtmChangeImpact.SOMEWHAT][i]
        : "",
      keyed[UtmChangeImpact.NOT_IMPACTED][i]
        ? keyed[UtmChangeImpact.NOT_IMPACTED][i]
        : "",
    ]);
  }

  return (
    <table className="table-striped table-sm table-fixed bg-white w-full">
      <thead>
        <tr>
          {[
            "Highly impacted by UTM update",
            "Somewhat impacted by UTM update",
            "Not impacted by UTM update",
          ].map((heading, index) => (
            <th
              key={index}
              className="pl-2 py-2 h-4 text-black/opacity-50 text-xs font-semibold border"
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row: any, index: number) => (
          <tr
            key={index}
            className="odd:bg-[#FAFAFF] even:bg-white border border-neutral-300"
          >
            <td className="border">
              {row[0] && <UtmChangeImpactPlatformCell platform={row[0].name} />}
            </td>
            <td className="border">
              {row[1] && <UtmChangeImpactPlatformCell platform={row[1].name} />}
            </td>
            <td className="border">
              {row[2] && <UtmChangeImpactPlatformCell platform={row[2].name} />}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
